import { useState, useMemo, useEffect } from "react";

/**
 * MissionAdminPanel — Panneau d'administration des notes de frais / rapports de mission.
 * Extrait du monolithe App.js pour amorcer la découpe du fichier de ~10 000 lignes.
 *
 * Toutes les données (état, handlers, refs) sont passées via `props` pour éviter
 * la réplication d'un contexte lourd. Aucune règle métier n'a été modifiée.
 */
export const MissionAdminPanel = ({
  // data & state
  API, pinInput,
  adminMissionReports, missionSelected, setMissionSelected,
  missionUploading,
  missionNewOpen, setMissionNewOpen,
  missionForm, setMissionForm,
  missionEditMode, setMissionEditMode, missionEditItems, setMissionEditItems,
  signaturePadOpen, setSignaturePadOpen, signaturePadRef,
  // handlers
  fetchAdminMissionReports, exportMissionCSV,
  createMissionReport, deleteMissionReport,
  uploadMissionAttachment, deleteMissionAttachment,
  updateMissionStatus,
  startMissionEdit, saveMissionEdit,
  openSignaturePad, sigStart, sigMove, sigEnd, sigClear, saveSignature,
  // helpers
  MISSION_RATES, toEUR, fmtEUR, fmtFCFA, missionTotalEUR, missionTotalFCFA,
}) => {
  // Local filter state
  const [filterTraveler, setFilterTraveler] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState("all");

  // Distinct values for filter dropdowns
  const travelers = useMemo(
    () => Array.from(new Set(adminMissionReports.map(r => r.traveler_name))).sort(),
    [adminMissionReports]
  );
  const years = useMemo(
    () => Array.from(new Set(
      adminMissionReports.flatMap(r => [r.start_date?.slice(0, 4), r.end_date?.slice(0, 4)].filter(Boolean))
    )).sort().reverse(),
    [adminMissionReports]
  );

  // Filtered reports
  const filteredReports = useMemo(() => adminMissionReports.filter(r => {
    if (filterTraveler && r.traveler_name !== filterTraveler) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (filterYear !== "all") {
      const y = filterYear;
      const start = r.start_date?.slice(0, 4);
      const end = r.end_date?.slice(0, 4);
      if (start !== y && end !== y) return false;
    }
    return true;
  }), [adminMissionReports, filterTraveler, filterStatus, filterYear]);

  // Financial summary — approved reports of current year
  const currentYear = new Date().getFullYear().toString();
  const approvedThisYear = useMemo(
    () => adminMissionReports.filter(r => r.status === "approved" && (r.start_date?.startsWith(currentYear) || r.end_date?.startsWith(currentYear))),
    [adminMissionReports, currentYear]
  );
  const totalApprovedEUR = approvedThisYear.reduce((s, r) => s + missionTotalEUR(r), 0);
  const totalApprovedFCFA = totalApprovedEUR * MISSION_RATES.EUR_XOF;
  const totalAllReportsEUR = adminMissionReports.reduce((s, r) => s + missionTotalEUR(r), 0);

  // Monthly expenses aggregation (last 12 months)
  const monthlyData = useMemo(() => {
    const map = {};
    for (const r of adminMissionReports) {
      const monthKey = (r.start_date || "").slice(0, 7); // YYYY-MM
      if (!monthKey) continue;
      map[monthKey] = (map[monthKey] || 0) + missionTotalEUR(r);
    }
    const keys = Object.keys(map).sort();
    return keys.map(k => ({ month: k, value: map[k] }));
  }, [adminMissionReports, missionTotalEUR]);

  const monthlyMax = Math.max(1, ...monthlyData.map(d => d.value));

  // Weekly digest state
  const [digestSending, setDigestSending] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [waSending, setWaSending] = useState(false);
  const [waResult, setWaResult] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);
  const previewDigest = () => {
    window.open(`${API}/mission/weekly-digest/preview?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, "_blank");
  };
  const sendDigestNow = async () => {
    if (!window.confirm("Envoyer le résumé hebdomadaire par email maintenant ?")) return;
    setDigestSending(true);
    setDigestResult(null);
    try {
      const r = await fetch(`${API}/mission/weekly-digest/send?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, { method: "POST" });
      const data = await r.json();
      setDigestResult(data.smtp);
    } catch (e) {
      setDigestResult({ sent: false, reason: e.message });
    } finally {
      setDigestSending(false);
    }
  };
  const sendWhatsAppNow = async () => {
    if (!window.confirm("Envoyer le résumé hebdomadaire par WhatsApp maintenant ?")) return;
    setWaSending(true);
    setWaResult(null);
    try {
      const r = await fetch(`${API}/mission/whatsapp/send?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`, { method: "POST" });
      const data = await r.json();
      setWaResult(data.twilio);
    } catch (e) {
      setWaResult({ sent: false, reason: e.message });
    } finally {
      setWaSending(false);
    }
  };
  const loadConfigStatus = async () => {
    try {
      const r = await fetch(`${API}/mission/config-status?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`);
      setConfigStatus(await r.json());
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadConfigStatus(); }, []); // eslint-disable-line

  return (
  <div data-testid="admin-mission-panel">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 className="font-heading text-xl font-bold text-gray-900 flex items-center gap-2">
        ✈️ Notes de frais &amp; Rapports de mission
      </h3>
      <div className="flex gap-2">
        <button
          onClick={fetchAdminMissionReports}
          className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold"
          data-testid="admin-mission-refresh"
        >
          🔄 Rafraîchir
        </button>
        <button
          onClick={exportMissionCSV}
          className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded font-semibold"
          data-testid="admin-mission-export-csv"
        >
          📊 Export CSV (Excel)
        </button>
        <a
          href={`${API}/mission/annual-report/${new Date().getFullYear()}?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`}
          target="_blank" rel="noreferrer"
          className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 rounded font-semibold"
          data-testid="admin-mission-annual-report"
        >
          🏛️ Bilan Annuel {new Date().getFullYear()}
        </a>
        <a
          href="/docs/SAFUAYE_Rapport_Mission_Togo_2026.pdf"
          target="_blank" rel="noreferrer"
          className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded font-semibold"
          data-testid="admin-mission-download-master-pdf"
        >
          📄 PDF officiel Togo 2026
        </a>
        <button
          onClick={() => setMissionNewOpen(true)}
          className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded font-semibold"
          data-testid="admin-mission-new"
        >
          ➕ Nouveau rapport
        </button>
      </div>
    </div>

    <div className="bg-emerald-50 border-l-4 border-emerald-600 p-3 rounded mb-4 text-xs text-emerald-900">
      💰 <strong>Comptabilité voyages officiels.</strong> Chaque déplacement financé par l&apos;association ou remboursé par elle doit être documenté ici avec ses justificatifs Mixx, factures et reçus. Ces archives seront présentées à l&apos;auditeur externe et à la Charity Commission UK.
      <div className="mt-1 text-emerald-700">Taux appliqués : 1 GBP = {MISSION_RATES.GBP_EUR} € · 1 € = {MISSION_RATES.EUR_XOF} FCFA (parité fixe BCEAO)</div>
    </div>

    {/* Financial summary card */}
    <div className="grid md:grid-cols-3 gap-3 mb-4" data-testid="admin-mission-financial-summary">
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-lg p-4 shadow-md">
        <div className="text-[10px] uppercase tracking-wider text-emerald-100 font-semibold">Total dépensé en {currentYear}</div>
        <div className="mt-1 text-2xl font-heading font-bold">{fmtEUR(totalApprovedEUR)}</div>
        <div className="text-xs text-emerald-100 mt-1">{fmtFCFA(totalApprovedFCFA)}</div>
        <div className="text-[10px] text-emerald-200 mt-2">Rapports approuvés uniquement ({approvedThisYear.length})</div>
      </div>
      <div className="bg-white border-2 border-emerald-200 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Cumul tous rapports</div>
        <div className="mt-1 text-2xl font-heading font-bold text-gray-900">{fmtEUR(totalAllReportsEUR)}</div>
        <div className="text-xs text-gray-500 mt-1">Toutes années, tous statuts</div>
        <div className="text-[10px] text-gray-500 mt-2">{adminMissionReports.length} rapport(s) au total</div>
      </div>
      <div className="bg-white border-2 border-indigo-200 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold">Rapports par statut</div>
        <div className="mt-2 space-y-1">
          {["draft", "submitted", "approved"].map(s => {
            const n = adminMissionReports.filter(r => r.status === s).length;
            const label = s === "draft" ? "Brouillons" : s === "submitted" ? "Soumis" : "Approuvés";
            const color = s === "approved" ? "bg-green-100 text-green-800" : s === "submitted" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700";
            return (
              <div key={s} className="flex justify-between items-center text-xs">
                <span className="text-gray-700">{label}</span>
                <span className={`font-semibold px-2 py-0.5 rounded ${color}`}>{n}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>

    {/* Monthly bar chart */}
    {monthlyData.length > 0 && (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="admin-mission-monthly-chart">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-heading text-sm font-bold text-gray-800">📈 Dépenses par mois</h4>
          <span className="text-[10px] text-gray-500">Somme des rapports (tous statuts) par mois de départ</span>
        </div>
        <div className="flex items-end gap-2 h-40">
          {monthlyData.map((d) => {
            const heightPct = Math.max(4, (d.value / monthlyMax) * 100);
            return (
              <div key={d.month} className="flex-1 flex flex-col items-center gap-1" title={`${d.month} : ${fmtEUR(d.value)}`}>
                <div className="text-[10px] font-semibold text-emerald-700">{d.value > 0 ? fmtEUR(d.value).replace('\u00A0', ' ') : ''}</div>
                <div
                  className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t hover:from-emerald-700 hover:to-emerald-500 transition-colors"
                  style={{ height: `${heightPct}%` }}
                />
                <div className="text-[10px] text-gray-600 whitespace-nowrap">{d.month}</div>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {/* Notification center — email + WhatsApp + config status */}
    <div className="bg-white border-2 border-indigo-200 rounded-lg p-4 mb-4" data-testid="admin-mission-digest-bar">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="font-heading text-sm font-bold text-indigo-900 flex items-center gap-2">
          📬 Centre de notifications hebdomadaires
        </h4>
        <span className="text-[10px] text-indigo-700">Cron auto : <b>dimanche 08:00 UTC</b></span>
      </div>

      {/* Config status pills */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
        <span className="text-gray-600 font-semibold">Configuration :</span>
        <span className={`px-2 py-0.5 rounded font-semibold ${configStatus?.smtp?.configured ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          📧 SMTP {configStatus?.smtp?.configured ? "✅ configuré" : "⚠️ non configuré"}
        </span>
        <span className={`px-2 py-0.5 rounded font-semibold ${configStatus?.twilio?.configured ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          💬 WhatsApp/Twilio {configStatus?.twilio?.configured ? "✅ configuré" : "⚠️ non configuré"}
        </span>
        {configStatus?.smtp?.mail_to && <span className="text-gray-500">→ {configStatus.smtp.mail_to}</span>}
        {configStatus?.twilio?.to && <span className="text-gray-500">→ {configStatus.twilio.to}</span>}
        <button onClick={loadConfigStatus}
                className="ml-auto text-indigo-600 hover:underline"
                data-testid="admin-mission-config-refresh">↻ Actualiser</button>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button onClick={previewDigest}
                className="text-xs bg-white border border-indigo-300 text-indigo-800 hover:bg-indigo-50 px-3 py-2 rounded font-semibold"
                data-testid="admin-mission-digest-preview">👁️ Aperçu email</button>
        <button onClick={sendDigestNow} disabled={digestSending}
                className={`text-xs px-3 py-2 rounded font-semibold ${digestSending ? "bg-gray-200 text-gray-500" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}
                data-testid="admin-mission-digest-send">
          {digestSending ? "⏳ Envoi…" : "📧 Envoyer par email"}
        </button>
        <button onClick={sendWhatsAppNow} disabled={waSending}
                className={`text-xs px-3 py-2 rounded font-semibold ${waSending ? "bg-gray-200 text-gray-500" : "bg-green-600 hover:bg-green-700 text-white"}`}
                data-testid="admin-mission-whatsapp-send">
          {waSending ? "⏳ Envoi…" : "💬 Envoyer par WhatsApp"}
        </button>
      </div>

      {digestResult && (
        <div className={`mt-2 px-2 py-1 rounded text-xs ${digestResult.sent ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          📧 {digestResult.sent ? "✅ " : "⚠️ "}{digestResult.reason}
        </div>
      )}
      {waResult && (
        <div className={`mt-2 px-2 py-1 rounded text-xs ${waResult.sent ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          💬 {waResult.sent ? "✅ " : "⚠️ "}{waResult.reason}
          {waResult.sid && <span className="ml-2 text-gray-500">SID: {waResult.sid}</span>}
        </div>
      )}
    </div>

    {/* Filter bar */}
    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-center text-xs" data-testid="admin-mission-filters">
      <span className="font-semibold text-gray-700">🔎 Filtrer :</span>
      <select
        value={filterTraveler}
        onChange={e => setFilterTraveler(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1"
        data-testid="admin-mission-filter-traveler"
      >
        <option value="">Tous les voyageurs</option>
        {travelers.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <select
        value={filterStatus}
        onChange={e => setFilterStatus(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1"
        data-testid="admin-mission-filter-status"
      >
        <option value="all">Tous statuts</option>
        <option value="draft">Brouillon</option>
        <option value="submitted">Soumis</option>
        <option value="approved">Approuvé</option>
      </select>
      <select
        value={filterYear}
        onChange={e => setFilterYear(e.target.value)}
        className="border border-gray-300 rounded px-2 py-1"
        data-testid="admin-mission-filter-year"
      >
        <option value="all">Toutes années</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <span className="text-gray-500 ml-auto">
        {filteredReports.length} / {adminMissionReports.length} rapport(s)
      </span>
      {(filterTraveler || filterStatus !== "all" || filterYear !== "all") && (
        <button
          onClick={() => { setFilterTraveler(""); setFilterStatus("all"); setFilterYear("all"); }}
          className="text-red-600 hover:underline"
          data-testid="admin-mission-filter-clear"
        >Effacer</button>
      )}
    </div>

    {missionNewOpen && (
      <div className="bg-white border-2 border-emerald-300 rounded-lg p-4 mb-4">
        <h4 className="font-heading text-lg font-bold text-emerald-800 mb-3">Nouveau rapport de mission</h4>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-gray-700 font-semibold">Voyageur *</span>
            <input type="text" value={missionForm.traveler_name}
              onChange={e => setMissionForm({ ...missionForm, traveler_name: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1"
              data-testid="mission-form-traveler" />
          </label>
          <label className="block">
            <span className="text-gray-700 font-semibold">Fonction</span>
            <input type="text" value={missionForm.traveler_role}
              onChange={e => setMissionForm({ ...missionForm, traveler_role: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
          <label className="block">
            <span className="text-gray-700 font-semibold">Date début *</span>
            <input type="date" value={missionForm.start_date}
              onChange={e => setMissionForm({ ...missionForm, start_date: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
          <label className="block">
            <span className="text-gray-700 font-semibold">Date fin *</span>
            <input type="date" value={missionForm.end_date}
              onChange={e => setMissionForm({ ...missionForm, end_date: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-gray-700 font-semibold">Lieu(x)</span>
            <input type="text" value={missionForm.location}
              onChange={e => setMissionForm({ ...missionForm, location: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-gray-700 font-semibold">Objet de la mission</span>
            <textarea value={missionForm.purpose}
              onChange={e => setMissionForm({ ...missionForm, purpose: e.target.value })}
              className="mt-1 w-full border border-gray-300 rounded px-2 py-1" rows={2} />
          </label>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-800">Postes de dépense</span>
            <button
              onClick={() => setMissionForm({
                ...missionForm,
                items: [...missionForm.items, { category: "local", label: "", amount: 0, currency: "XOF", beneficiary: "", date: "", notes: "" }]
              })}
              className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded"
            >
              + ajouter une ligne
            </button>
          </div>
          <div className="space-y-2">
            {missionForm.items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center text-xs">
                <select value={it.category}
                  onChange={e => { const items = [...missionForm.items]; items[idx].category = e.target.value; setMissionForm({ ...missionForm, items }); }}
                  className="col-span-2 border border-gray-300 rounded px-1 py-1">
                  <option value="airfare">✈️ Transport</option>
                  <option value="transfer">💸 Transfert</option>
                  <option value="local">🏨 Local</option>
                </select>
                <input type="text" placeholder="Libellé" value={it.label}
                  onChange={e => { const items = [...missionForm.items]; items[idx].label = e.target.value; setMissionForm({ ...missionForm, items }); }}
                  className="col-span-5 border border-gray-300 rounded px-1 py-1" />
                <input type="number" step="0.01" placeholder="Montant" value={it.amount}
                  onChange={e => { const items = [...missionForm.items]; items[idx].amount = e.target.value; setMissionForm({ ...missionForm, items }); }}
                  className="col-span-2 border border-gray-300 rounded px-1 py-1" />
                <select value={it.currency}
                  onChange={e => { const items = [...missionForm.items]; items[idx].currency = e.target.value; setMissionForm({ ...missionForm, items }); }}
                  className="col-span-1 border border-gray-300 rounded px-1 py-1">
                  <option value="XOF">FCFA</option>
                  <option value="EUR">€</option>
                  <option value="GBP">£</option>
                </select>
                <input type="text" placeholder="Bénéf." value={it.beneficiary}
                  onChange={e => { const items = [...missionForm.items]; items[idx].beneficiary = e.target.value; setMissionForm({ ...missionForm, items }); }}
                  className="col-span-1 border border-gray-300 rounded px-1 py-1" />
                <button
                  onClick={() => { const items = missionForm.items.filter((_, i) => i !== idx); setMissionForm({ ...missionForm, items: items.length ? items : [{ category: "local", label: "", amount: 0, currency: "XOF", beneficiary: "", date: "", notes: "" }] }); }}
                  className="col-span-1 text-red-600 hover:text-red-800 text-lg"
                  aria-label="Supprimer">✕</button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={() => setMissionNewOpen(false)}
            className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded">Annuler</button>
          <button onClick={createMissionReport}
            className="text-sm bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded font-semibold"
            data-testid="mission-form-save">💾 Enregistrer</button>
        </div>
      </div>
    )}

    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 bg-white border border-gray-200 rounded-lg p-3 max-h-[70vh] overflow-y-auto">
        {filteredReports.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            {adminMissionReports.length === 0 ? "Aucun rapport enregistré." : "Aucun rapport ne correspond aux filtres."}
          </p>
        ) : (
          <ul className="space-y-2">
            {filteredReports.map(rep => (
              <li key={rep.id}>
                <button
                  onClick={() => setMissionSelected(rep)}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    missionSelected?.id === rep.id
                      ? "border-emerald-600 bg-emerald-50"
                      : "border-gray-200 hover:border-emerald-300 hover:bg-gray-50"
                  }`}
                  data-testid={`admin-mission-item-${rep.id}`}
                >
                  <div className="font-semibold text-sm text-gray-900">{rep.traveler_name}</div>
                  <div className="text-xs text-gray-600">{rep.start_date} → {rep.end_date}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">{rep.location || rep.purpose?.slice(0, 60)}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                      rep.status === "approved" ? "bg-green-100 text-green-800" :
                      rep.status === "submitted" ? "bg-blue-100 text-blue-800" :
                      "bg-gray-100 text-gray-700"
                    }`}>{rep.status}</span>
                    <span className="text-[10px] text-gray-500">{(rep.attachments || []).length} pièce(s)</span>
                  </div>
                  <div className="text-xs font-semibold text-emerald-700 mt-1">{fmtEUR(missionTotalEUR(rep))} · {fmtFCFA(missionTotalFCFA(rep))}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-4">
        {!missionSelected ? (
          <p className="text-sm text-gray-500 text-center py-8">← Sélectionnez un rapport pour voir le détail.</p>
        ) : (
          <div>
            <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
              <div>
                <h4 className="font-heading text-lg font-bold text-gray-900">{missionSelected.traveler_name}</h4>
                <p className="text-xs text-gray-600">{missionSelected.traveler_role}</p>
                <p className="text-xs text-gray-500 mt-1">{missionSelected.start_date} → {missionSelected.end_date} · {missionSelected.location}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={missionSelected.status || "draft"}
                  onChange={e => updateMissionStatus(missionSelected.id, e.target.value)}
                  className="text-xs border border-gray-300 rounded px-2 py-1"
                >
                  <option value="draft">Brouillon</option>
                  <option value="submitted">Soumis</option>
                  <option value="approved">Approuvé</option>
                </select>
                {!missionEditMode ? (
                  <button
                    onClick={startMissionEdit}
                    className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded font-semibold"
                    data-testid="admin-mission-edit-start"
                  >✏️ Modifier</button>
                ) : (
                  <>
                    <button
                      onClick={saveMissionEdit}
                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded font-semibold"
                      data-testid="admin-mission-edit-save"
                    >💾 Enregistrer</button>
                    <button
                      onClick={() => { setMissionEditMode(false); setMissionEditItems([]); }}
                      className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2 py-1 rounded font-semibold"
                    >Annuler</button>
                  </>
                )}
                <button
                  onClick={openSignaturePad}
                  className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded font-semibold"
                  data-testid="admin-mission-sign"
                >✍️ Signer</button>
                <a
                  href={`${API}/mission/expense-reports/${missionSelected.id}/signed-pdf?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded font-semibold"
                  data-testid="admin-mission-download-signed-pdf"
                >📄 PDF signé</a>
                <a
                  href={`${API}/mission/expense-reports/${missionSelected.id}/signed-docx?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs bg-sky-600 hover:bg-sky-700 text-white px-2 py-1 rounded font-semibold"
                  data-testid="admin-mission-download-signed-docx"
                >📝 DOCX signé</a>
                <button
                  onClick={() => deleteMissionReport(missionSelected.id)}
                  className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded font-semibold"
                  data-testid="admin-mission-delete"
                >🗑️ Supprimer</button>
              </div>
            </div>

            {missionSelected.purpose && (
              <p className="text-sm text-gray-700 bg-gray-50 border-l-4 border-emerald-500 p-2 rounded mb-3">{missionSelected.purpose}</p>
            )}

            <h5 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">Postes de dépense</h5>
            {missionEditMode ? (
              <div className="mb-4">
                <div className="space-y-2 mb-2">
                  {missionEditItems.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center text-xs">
                      <select value={it.category}
                        onChange={e => { const items = [...missionEditItems]; items[idx].category = e.target.value; setMissionEditItems(items); }}
                        className="col-span-2 border border-gray-300 rounded px-1 py-1">
                        <option value="airfare">✈️ Transport</option>
                        <option value="transfer">💸 Transfert</option>
                        <option value="local">🏨 Local</option>
                      </select>
                      <input type="text" placeholder="Libellé" value={it.label || ""}
                        onChange={e => { const items = [...missionEditItems]; items[idx].label = e.target.value; setMissionEditItems(items); }}
                        className="col-span-4 border border-gray-300 rounded px-1 py-1" />
                      <input type="number" step="0.01" placeholder="Montant" value={it.amount || 0}
                        onChange={e => { const items = [...missionEditItems]; items[idx].amount = e.target.value; setMissionEditItems(items); }}
                        className="col-span-2 border border-gray-300 rounded px-1 py-1" />
                      <select value={it.currency || "XOF"}
                        onChange={e => { const items = [...missionEditItems]; items[idx].currency = e.target.value; setMissionEditItems(items); }}
                        className="col-span-1 border border-gray-300 rounded px-1 py-1">
                        <option value="XOF">FCFA</option>
                        <option value="EUR">€</option>
                        <option value="GBP">£</option>
                      </select>
                      <input type="text" placeholder="Bénéf." value={it.beneficiary || ""}
                        onChange={e => { const items = [...missionEditItems]; items[idx].beneficiary = e.target.value; setMissionEditItems(items); }}
                        className="col-span-2 border border-gray-300 rounded px-1 py-1" />
                      <button
                        onClick={() => setMissionEditItems(missionEditItems.filter((_, i) => i !== idx))}
                        className="col-span-1 text-red-600 hover:text-red-800 text-lg"
                        aria-label="Supprimer">✕</button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setMissionEditItems([...missionEditItems, { category: "local", label: "", amount: 0, currency: "XOF", beneficiary: "", date: "", notes: "" }])}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded"
                  data-testid="admin-mission-edit-add-line"
                >
                  + Ajouter une ligne
                </button>
                <div className="mt-2 text-xs text-gray-600 italic">
                  Cliquez sur <b>💾 Enregistrer</b> en haut pour appliquer les changements.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">Cat.</th>
                      <th className="px-2 py-1 text-left">Libellé</th>
                      <th className="px-2 py-1 text-right">Montant</th>
                      <th className="px-2 py-1 text-right">≈ EUR</th>
                      <th className="px-2 py-1 text-left">Bénéf.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(missionSelected.items || []).map(it => (
                      <tr key={it.id} className="border-t border-gray-100">
                        <td className="px-2 py-1">
                          {it.category === "airfare" ? "✈️" : it.category === "transfer" ? "💸" : "🏨"}
                        </td>
                        <td className="px-2 py-1 text-gray-800">{it.label}</td>
                        <td className="px-2 py-1 text-right font-mono">{Number(it.amount).toLocaleString('fr-FR')} {it.currency}</td>
                        <td className="px-2 py-1 text-right text-gray-600">{fmtEUR(toEUR(it.amount, it.currency))}</td>
                        <td className="px-2 py-1 text-gray-600">{it.beneficiary || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-emerald-50 font-bold">
                    <tr>
                      <td colSpan={3} className="px-2 py-1 text-right">TOTAL</td>
                      <td className="px-2 py-1 text-right text-emerald-800">{fmtEUR(missionTotalEUR(missionSelected))}</td>
                      <td className="px-2 py-1 text-emerald-800">{fmtFCFA(missionTotalFCFA(missionSelected))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <h5 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                Justificatifs ({(missionSelected.attachments || []).length})
              </h5>
              <label className={`text-xs px-3 py-1 rounded font-semibold cursor-pointer ${missionUploading ? "bg-gray-200 text-gray-500" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                {missionUploading ? "⏳ Envoi…" : "📎 Ajouter des justificatifs"}
                <input type="file" multiple accept="image/*,application/pdf"
                  className="hidden"
                  disabled={missionUploading}
                  onChange={e => uploadMissionAttachment(missionSelected.id, e.target.files)}
                  data-testid="admin-mission-upload"
                />
              </label>
            </div>
            {(missionSelected.attachments || []).length === 0 ? (
              <p className="text-xs text-gray-500 italic">Aucun justificatif joint.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {(missionSelected.attachments || []).map(att => {
                  const isImg = (att.content_type || "").startsWith("image/");
                  const fileUrl = `${API}/mission/attachments/${att.id}/file?pin=${encodeURIComponent(pinInput || 'SAFUAYE2024')}`;
                  return (
                    <div key={att.id} className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {isImg ? (
                        <a href={fileUrl} target="_blank" rel="noreferrer">
                          <img src={fileUrl} alt={att.label}
                            className="w-full h-24 object-cover rounded" loading="lazy" />
                        </a>
                      ) : (
                        <a href={fileUrl} target="_blank" rel="noreferrer"
                          className="flex items-center justify-center h-24 bg-white border rounded text-3xl">📄</a>
                      )}
                      <div className="mt-1 text-[10px] text-gray-700 truncate" title={att.label}>{att.label}</div>
                      <div className="flex items-center justify-between mt-1">
                        <a href={fileUrl} target="_blank" rel="noreferrer" className="text-[10px] text-emerald-700 hover:underline">👁️ Voir</a>
                        <button onClick={() => deleteMissionAttachment(att.id)}
                          className="text-[10px] text-red-600 hover:text-red-800">🗑️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {signaturePadOpen && missionSelected && (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
           onClick={() => setSignaturePadOpen(false)}
           data-testid="admin-mission-sign-modal">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-5"
             onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-bold text-gray-900">✍️ Signature — {missionSelected.traveler_name}</h3>
            <button onClick={() => setSignaturePadOpen(false)} className="text-gray-500 hover:text-gray-800 text-xl">×</button>
          </div>
          <p className="text-xs text-gray-600 mb-2">Signez dans le cadre ci-dessous (souris ou doigt sur écran tactile). Après enregistrement, la signature sera archivée comme pièce jointe et le rapport passera au statut <b>Approuvé</b>.</p>
          <div className="border-2 border-dashed border-indigo-400 rounded-lg overflow-hidden bg-white">
            <canvas
              ref={signaturePadRef}
              onMouseDown={sigStart} onMouseMove={sigMove} onMouseUp={sigEnd} onMouseLeave={sigEnd}
              onTouchStart={sigStart} onTouchMove={sigMove} onTouchEnd={sigEnd}
              style={{ width: "100%", height: "200px", touchAction: "none", cursor: "crosshair" }}
              data-testid="admin-mission-sign-canvas"
            />
          </div>
          <div className="mt-3 flex justify-between items-center">
            <button onClick={sigClear} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded font-semibold">🧹 Effacer</button>
            <div className="flex gap-2">
              <button onClick={() => setSignaturePadOpen(false)} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded">Annuler</button>
              <button onClick={saveSignature}
                      className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded font-semibold"
                      data-testid="admin-mission-sign-save">
                💾 Signer &amp; approuver
              </button>
            </div>
          </div>
          <p className="mt-3 text-[10px] text-gray-500 italic">Signature numérique horodatée · Conforme aux exigences d&apos;archivage de la Charity Commission UK.</p>
        </div>
      </div>
    )}
  </div>
  );
};

export default MissionAdminPanel;
